import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Checkbox from "@mui/material/Checkbox";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import PollIcon from '@mui/icons-material/Poll';
import { dateToHumanDateStr } from "../shared/date.js";

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];
const STORAGE_KEY = "upvotes";

const readUpvotes = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const Poll = ({ date, venues = [] }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState([]);
    const [editing, setEditing] = useState(false);

    const loadResults = async () => {
        const totals = {};
        try {
            const res = await fetch(`${BACKEND_HOST}/system/getPollResults`);
            const merged = res.ok ? await res.json() : {};
            for (const [venueKey, total] of Object.entries(merged?.[date] ?? {})) {
                totals[date + "-" + venueKey] = total;
            }
        } catch (err) {
            console.error("poll results failed", err);
        }
        setResults(totals);
    };

    const votedKeys = () => {
        const upvotes = readUpvotes();
        return venues.filter(v => (date + "-" + v.venueKey) in upvotes).map(v => v.venueKey);
    };

    const handleOpen = () => {
        setError(null);
        setOpen(true);
        setSelected(votedKeys());
        setEditing(false);
        const upvotes = readUpvotes();
        if (venues.some(v => (date + "-" + v.venueKey) in upvotes)) {
            loadResults();
        } else {
            setResults(null);
        }
    };

    const toggleSelected = (venueKey) => {
        setSelected(sel => sel.includes(venueKey) ? sel.filter(k => k !== venueKey) : [...sel, venueKey]);
    };

    const handleVote = async () => {
        setSubmitting(true);
        setError(null);
        const previous = votedKeys();
        const toAdd = selected.filter(k => !previous.includes(k));
        const toRemove = previous.filter(k => !selected.includes(k));
        let failed = false;
        const send = async (action, venueKey) => {
            try {
                const res = await fetch(`${BACKEND_HOST}/upvotes/${action}`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({date, venueKey})
                });
                if (!res.ok) throw new Error("HTTP " + res.status);
                const upvotes = readUpvotes();
                if (action === "add") {
                    upvotes[date + "-" + venueKey] = true;
                } else {
                    delete upvotes[date + "-" + venueKey];
                }
                localStorage.setItem(STORAGE_KEY, JSON.stringify(upvotes));
            } catch (err) {
                console.error("poll " + action + " failed", err);
                failed = true;
            }
        };
        for (const venueKey of toAdd) await send("add", venueKey);
        for (const venueKey of toRemove) await send("remove", venueKey);
        if (failed) {
            setError("Could not save all changes. Please try again.");
        } else {
            await loadResults();
            setEditing(false);
            setSelected(votedKeys());
        }
        setSubmitting(false);
    };

    const voted = readUpvotes();
    const showList = !results || editing;
    const totalVotes = results ? venues.reduce((sum, v) => sum + (results[date + "-" + v.venueKey] ?? 0), 0) : 0;
    const venuesWithVotes = results ? venues.filter(v => (results[date + "-" + v.venueKey] ?? 0) > 0) : [];
    const venuesWithoutVotes = results ? venues.filter(v => (results[date + "-" + v.venueKey] ?? 0) === 0) : [];

    return <>
        <Button variant="contained" size="small" startIcon={<PollIcon />} color={"warning"} onClick={handleOpen} sx={{
            animation: "pollBounce 0.8s ease 0.5s 3",
            "@keyframes pollBounce": {
                "0%, 100%": {transform: "translateY(0)"},
                "40%": {transform: "translateY(-12px)"},
                "60%": {transform: "translateY(-4px)"}
            }
        }}>POLL: Where are you dancing?</Button>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>
                <Typography variant="subtitle2" component="div" sx={{opacity: 0.8}}>{dateToHumanDateStr(new Date(date + "T12:00:00"))}</Typography>
                {!showList ? "Survey Says!" : "Where are you going? (select all that apply)"}
            </DialogTitle>
            <DialogContent>
                {error && <Typography color="error" variant="body2">{error}</Typography>}
                {submitting && <Box sx={{display: "flex", justifyContent: "center", p: 2}}><CircularProgress size={24}/></Box>}
                {!submitting && showList && <List>
                    {venues.map(v => <ListItemButton key={v.venueKey} onClick={() => toggleSelected(v.venueKey)}>
                        <ListItemIcon sx={{minWidth: 0}}>
                            <Checkbox edge="start" disableRipple tabIndex={-1} checked={selected.includes(v.venueKey)}/>
                        </ListItemIcon>
                        <ListItemText primary={v.venueName}/>
                    </ListItemButton>)}
                </List>}
                {!submitting && !showList && venuesWithVotes.map(v => {
                    const count = results[date + "-" + v.venueKey] ?? 0;
                    const percent = totalVotes ? Math.round(count / totalVotes * 100) : 0;
                    return <Box key={v.venueKey} sx={{mb: 1.5}}>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="body2">
                                {(date + "-" + v.venueKey) in voted && <span style={{color: "yellow", marginRight: "6px"}}>✓</span>}
                                {v.venueName}
                            </Typography>
                            <Typography variant="body2"><strong>{percent}%</strong></Typography>
                        </Box>
                        <Box sx={{height: 8, borderRadius: 1, background: "#393536"}}>
                            <Box sx={{height: "100%", borderRadius: 1, background: "lightblue", width: percent + "%"}}/>
                        </Box>
                    </Box>;
                })}
                {!submitting && !showList && venuesWithoutVotes.length > 0 && <Typography variant="body2" sx={{mb: 1.5, opacity: 0.8}}>
                    No votes: {venuesWithoutVotes.map(v => v.venueName).join(", ")}
                </Typography>}
                {!submitting && !showList && <Typography variant="body2" sx={{opacity: 0.8, textAlign: "right"}}>Total votes: {totalVotes}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>Close</Button>
                {!showList && <Button onClick={() => { setSelected(votedKeys()); setEditing(true); }}>Edit my picks</Button>}
                {showList && <Button variant="contained" disabled={submitting || (!selected.length && !votedKeys().length) || (selected.length === votedKeys().length && selected.every(k => votedKeys().includes(k)))} onClick={handleVote}>Vote</Button>}
            </DialogActions>
        </Dialog>
    </>;
};

export { Poll };
